import { collection, addDoc, getDocs, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { firestore, storage } from '../config/firebase';

const MY_FILES_SUB = 'myFiles';
const MY_FILES_STORAGE_PREFIX = 'users';

/**
 * Storage path for a user file: users/{uid}/my-files/{fileId}_{safeName}
 */
function getStoragePath(uid, fileId, fileName) {
  const safe = (fileName || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
  return `${MY_FILES_STORAGE_PREFIX}/${uid}/my-files/${fileId}_${safe}`;
}

function myFilesRef(uid) {
  return collection(firestore, 'users', uid, MY_FILES_SUB);
}

/**
 * List all file refs for the user (one-time).
 * @param {string} uid
 * @returns {Promise<Array<{ id: string, storagePath: string, name: string, uploadedAt: string, label?: string }>>}
 */
export async function getMyFilesOnce(uid) {
  if (!uid) return [];
  const snapshot = await getDocs(myFilesRef(uid));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to user's file list.
 * @param {string} uid
 * @param {(files: Array<{ id: string, storagePath: string, name: string, uploadedAt: string }>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeMyFiles(uid, callback) {
  if (!uid) {
    callback([]);
    return () => {};
  }
  return onSnapshot(
    myFilesRef(uid),
    (snapshot) => {
      const files = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(files);
    },
    (err) => {
      console.warn('subscribeMyFiles error', err);
      callback([]);
    }
  );
}

/**
 * Upload a file to user's "Mes Fichiers" and add a doc in users/{uid}/myFiles.
 * @param {string} uid
 * @param {File} file
 * @param {string} [label] - optional label for display
 * @returns {Promise<{ id: string, storagePath: string, name: string, uploadedAt: string }>}
 */
export async function uploadMyFile(uid, file, label = '') {
  if (!uid || !file) throw new Error('uid and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const storagePath = getStoragePath(uid, fileId, file.name);
  const storageRefPath = ref(storage, storagePath);
  await uploadBytes(storageRefPath, file);
  const uploadedAt = new Date().toISOString();
  const col = myFilesRef(uid);
  const docRef = await addDoc(col, {
    storagePath,
    name: file.name || 'file',
    uploadedAt,
    ...(label ? { label } : {}),
  });
  return { id: docRef.id, storagePath, name: file.name || 'file', uploadedAt, label };
}

/**
 * Delete a file from Storage and remove its doc.
 * @param {string} uid
 * @param {string} fileId - doc id in myFiles subcollection
 * @param {{ storagePath: string }} fileRef
 */
export async function deleteMyFile(uid, fileId, fileRef) {
  if (!uid || !fileId || !fileRef?.storagePath) return;
  try {
    const r = ref(storage, fileRef.storagePath);
    await deleteObject(r);
  } catch (e) {
    console.warn('deleteMyFile: deleteObject failed', e);
  }
  const docRef = doc(firestore, 'users', uid, MY_FILES_SUB, fileId);
  await deleteDoc(docRef);
}

/**
 * Get download URL for a file in Mes Fichiers.
 * @param {string} storagePath
 * @returns {Promise<string>}
 */
export async function getMyFileDownloadUrl(storagePath) {
  if (!storagePath) throw new Error('storagePath required');
  const r = ref(storage, storagePath);
  return getDownloadURL(r);
}
